import React from "react";
import { Users, Phone, Mail, Gift, PartyPopper, Calendar, Clock } from "lucide-react";

const PACKAGE_OPTIONS = [
  { value: "Basic", label: "Basic - Rs 3,999" },
  { value: "Classic", label: "Classic - Rs 5,999" },
  { value: "Premium", label: "Premium - Rs 8,999" },
  { value: "Luxury", label: "Luxury - Rs 14,999" },
  { value: "Custom", label: "Custom - Build Your Party" },
];

const SETUP_TIME_OPTIONS = [
  "Morning (9 AM - 12 PM)",
  "Afternoon (12 PM - 4 PM)",
  "Evening (4 PM - 8 PM)",
  "Night (8 PM - 12 AM)",
];

/**
 * BookingFormFields — Customer info + Occasion/Package + Date/Time groups.
 * Pure controlled component; parent owns `formData` and `setFormData`.
 */
export default function BookingFormFields({ formData, setFormData, occasions }) {
  const upd = (patch) => setFormData({ ...formData, ...patch });

  return (
    <>
      {/* Customer details — name + phone */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
            <Users size={16} /> Name *
          </label>
          <input
            type="text"
            required
            value={formData.customer_name}
            onChange={(e) => upd({ customer_name: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none"
            placeholder="Your full name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
            <Phone size={16} /> Phone *
          </label>
          <input
            type="tel"
            required
            value={formData.customer_phone}
            onChange={(e) => upd({ customer_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
            inputMode="numeric"
            autoComplete="tel"
            maxLength={10}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none"
            placeholder="9876543210"
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
          <Mail size={16} /> Email *
        </label>
        <input
          type="email"
          required
          value={formData.customer_email}
          onChange={(e) => upd({ customer_email: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none"
          placeholder="your@email.com"
        />
      </div>

      {/* Occasion + Package */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
            <Gift size={16} /> Occasion *
          </label>
          <select
            value={formData.occasion}
            onChange={(e) => upd({ occasion: e.target.value, occasion_name: '' })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none"
            data-testid="occasion-select"
          >
            {occasions.map(occ => <option key={occ} value={occ}>{occ}</option>)}
          </select>
          {formData.occasion === 'Other' && (
            <input
              type="text"
              required
              value={formData.occasion_name}
              onChange={(e) => upd({ occasion_name: e.target.value })}
              className="w-full mt-2 px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] focus:ring-2 focus:ring-[#E83E8C]/20 outline-none"
              placeholder="What's the party for? e.g. Farewell, Reunion, Promotion"
              data-testid="occasion-name-input"
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
            <PartyPopper size={16} /> Package *
          </label>
          <select
            value={formData.package}
            onChange={(e) => upd({ package: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none"
            data-testid="package-select"
          >
            <option value="">Select Package</option>
            {PACKAGE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </div>

      {/* Date + Setup time */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
            <Calendar size={16} /> Selected Date *
          </label>
          <input
            type="text"
            readOnly
            value={formData.date ? new Date(formData.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Please select a date from calendar'}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2">
            <Clock size={16} /> Setup Time *
          </label>
          <select
            value={formData.setup_time}
            onChange={(e) => upd({ setup_time: e.target.value })}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none"
            data-testid="setup-time-select"
          >
            <option value="">Select setup time</option>
            {SETUP_TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
    </>
  );
}
